import React, {
  RefObject,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from 'react';
import clsx from 'clsx';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MenuItem from '@mui/material/MenuItem';
import {
  Dayjs,
} from 'utils/dayjs';
import {
  useFormContext,
} from 'react-hook-form';
import pick from 'lodash/pick';
import values from 'lodash/values';
import isNil from 'lodash/isNil';
import {
  InsuranceDTO,
  InsuranceSlot,
  InsuranceMSPTypeSettingDTO,
  PatientDTO,
} from '@/dtos';
import Controller from 'core/components/FormController';
import {
  isEmptyString,
} from 'utils/misc';
import Accordion from 'core/components/Accordion';
import Input from 'components/Input';
import Select from 'core/components/Select';
import Wrapper from 'components/Input/Wrapper';
import AddressFields from 'components/AddressFields';
import DemographicFields from 'components/DemographicFields';
import PhoneInput from 'core/components/PhoneInput';
import {
  Payer,
  validatePhone,
} from 'utils/helper';
import {
  getPatientDemographics,
} from 'utils/patient';
import InsuranceLookup from 'components/InsuranceLookup';
import CurrencyTextField from 'core/components/CurrencyTextField';
import {
  validateAddressInfo,
} from '@/utils/react-hook-form';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import head from 'lodash/head';
import {
  useInsuranceMetaData,
} from '@/components/hooks/insurances';
import DemographicsHeader from './DemographicsHeader';
import {
  useGetOrgByPatientId,
} from '@/hooks/useApi';
import {
  InfoIcon,
} from 'lucide-react';

export type Demographics = Pick<PatientDTO,
  'firstName'
  | 'middleName'
  | 'lastName'
  | 'genderDescription'
> & { dateOfBirth: Dayjs };

export type InsuranceDataForm = Omit<InsuranceDTO,
  'insuranceOwnerDOB'
  | 'mspType'
  | 'effectiveFrom'
  | 'effectiveTill'
> & {
  demographics?: Demographics;
  insuranceOwnerDOB?: Dayjs;
  effectiveFrom?: Dayjs;
  mspType?: InsuranceMSPTypeSettingDTO;
  payerEntryPayerId?: string | null;
};

type Props = {
  patient?: Partial<PatientDTO>;
  defaultValues?: InsuranceDataForm;
};

const commonAccordionProps = {
  expandIcon: null,
  disableGutters: true,
  className: '!border-0',
  headerClasses: {
    root: '!p-0 !text-xs font-semibold !border-0 !min-h-[initial]',
    content: '!m-0 !mb-2',
  },
  detailsClasses: { root: '!p-0' },
};

export const REQUIRED_ADDRESS_FIELDS = [
  'street',
  'city',
  'state',
  'zipCode',
];

export default function InsuranceForm({
  patient,
  defaultValues,
}: Props) {
  const {
    control,
    watch,
    reset,
    setValue,
    formState: { isDirty, dirtyFields },
  } = useFormContext<InsuranceDataForm>();

  const containerRef = useRef<HTMLDivElement>(null);
  const insuranceLookupRef = useRef<HTMLInputElement>(null);

  const formValue = watch();
  const {
    insuranceOwnerRelationshipType,
    demographics,
  } = formValue;

  const { address } = patient ?? {};
  const { phoneNumberWithAreaCode: phone } = patient?.phoneNumber ?? {};
  const hasAddress = !isNil(address) && values(
    pick(address, REQUIRED_ADDRESS_FIELDS),
  ).every((item) => !isEmptyString(item));
  const hasPhone = !isEmptyString(phone);

  const {
    relationshipTypes,
    relationshipTypesByName,
    mspTypes,
    insuranceSlotValues,
    getInsuranceSlotLabel,
  } = useInsuranceMetaData();
  const { data: org } = useGetOrgByPatientId(true);
  const officePhone = org?.organizationPhoneNumber?.phoneNumberWithAreaCode;

  const {
    isSubscriberPatient,
    isEmployerInsurance,
  } = relationshipTypesByName[insuranceOwnerRelationshipType ?? ''] ?? {};

  const isSelf = isSubscriberPatient || isEmployerInsurance;

  const [selectedPayerId, setSelectedPayerId] = useState<string | null>(null);

  const mspOptions = useMemo(() => ([
    { code: null, name: 'None' },
    ...(mspTypes ?? []),
  ]), [mspTypes]);

  useEffect(() => {
    if (isNil(insuranceOwnerRelationshipType)) {
      setValue('insuranceOwnerRelationshipType', head(relationshipTypes ?? []));
    }
  }, [relationshipTypes]);

  const updateDemographics = useCallback(() => {
    const newDemographics = isSelf
      ? getPatientDemographics(patient ?? {})
      : demographics;
    reset({
      ...formValue,
      demographics: newDemographics,
      insuranceOwnerPhoneNumber: isSelf && hasPhone
        ? patient?.phoneNumber?.phoneNumberWithAreaCode
        : formValue.insuranceOwnerPhoneNumber,
    }, { keepErrors: true });
  }, [isSelf, demographics, patient, formValue, hasPhone]);

  useEffect(() => {
    updateDemographics();
  }, []);

  useEffect(() => {
    if (isDirty) {
      updateDemographics();
    }
  }, [isDirty, isSelf]);
  return (
    <div
      className="flex flex-col gap-3 w-full populate-form"
      ref={containerRef as RefObject<HTMLDivElement>}
    >
      <div className="flex flex-col sm:flex-row gap-3">
        <Controller
          control={control}
          name="insuranceOwnerRelationshipType"
          rules={{ required: true }}
          render={({ field, fieldState: { invalid } }) => (
            <Select<string>
              {...field}
              error={invalid}
              showErrorText={false}
              getValue={(option) => option}
              getLabel={(option) => option}
              options={relationshipTypes ?? []}
              placeholder="Select"
              className="w-full sm:w-[calc(33.33%-8px)]"
              id="insurance-type"
              muiClasses={{ select: '!py-[7px]' }}
              label="Relationship to Subscriber"
            />
          )}
        />
        {isEmployerInsurance && (
          <Controller
            name="employerName"
            control={control}
            rules={{ required: 'Employer is required' }}
            render={({ field, fieldState: { invalid } }) => (
              <Input
                {...field}
                required
                error={invalid}
                showErrorText={false}
                id="subscriber-employer"
                className="w-full sm:w-[calc(33.33%-8px)]"
                label="Employer"
                type="text"
              />
            )}
          />
        )}
        <Controller
          control={control}
          name="mspType"
          render={({ field }) => (
            <Select<InsuranceMSPTypeSettingDTO>
              {...field}
              value={typeof field.value === 'object' ? field.value : { code: field.value }}
              getValue={(option) => option?.code ?? '--'}
              getLabel={(option) => (
                !isNil(option?.code)
                  ? `${option?.code ?? '--'} - ${option?.name ?? '--'}`
                  : 'None'
              )}
              options={mspOptions}
              placeholder="Select"
              className="w-full sm:w-[calc(33.33%-8px)]"
              id="msp-type"
              muiClasses={{ select: '!py-[7px]' }}
              label="MSP Type"
            />
          )}
        />
      </div>
      <Controller
        control={control}
        name="demographics"
        render={({ field, fieldState: { error } }) => (
          <Accordion
            expanded={!isSelf}
            defaultExpanded={false}
            header={isSelf
              ? (
                <DemographicsHeader
                  demographics={field?.value}
                  insuranceOwnerAddress={address ?? {}}
                  insuranceOwnerPhoneNumber={phone}
                  hasAddress={hasAddress}
                />
              )
              : 'Subscriber\'s Demographic Information'}
            {...commonAccordionProps}
          >
            <DemographicFields
              key={insuranceOwnerRelationshipType}
              {...field}
              error={error}
              disabled={isSelf}
            />
          </Accordion>
        )}
      />
      {(!isSelf || !hasAddress) && (
        <Controller
          control={control}
          name="insuranceOwnerAddress"
          rules={{ validate: (value) => (validateAddressInfo(value) || 'Provide valid address') }}
          render={({ field }) => (
            <Accordion
              expanded
              header="Subscriber's Address"
              {...commonAccordionProps}
            >
              <AddressFields
                {...field}
                disabled={isSelf && hasAddress}
              />
            </Accordion>
          )}
        />
      )}
      {(!isSelf || !hasPhone) && (
        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <Controller
            name="insuranceOwnerPhoneNumber"
            control={control}
            rules={{
              validate: (value) => (validatePhone(value) ? true : 'Subscriber\'s Phone is required'),
            }}
            render={({ field, fieldState: { error } }) => (
              <Wrapper
                className="w-full sm:w-1/3"
                error={!isNil(error)}
                label="Subscriber's Phone"
                id="subscribers-phone"
                showErrorText={false}
                required
              >
                <PhoneInput
                  {...field}
                  error={!isNil(error)}
                  id="phone"
                  name="phone"
                />
              </Wrapper>
            )}
          />
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 w-full">
        <div className='!w-full sm:!w-2/3'>
          <Controller
            name="insuranceName"
            control={control}
            rules={{ required: 'Payer is required' }}
            render={({ field, fieldState: { invalid } }) => (
              <InsuranceLookup
                {...field}
                onChange={(value: Payer) => {
                  field.onChange(value?.payerName ?? '');
                  if (!isNil(value?.payerEntryId)) {
                    setValue('payerEntryId', value?.payerEntryId);
                    setSelectedPayerId(value?.payerId ?? null);
                  }
                }}
                required
                error={invalid}
                id="insurance-name"
                className="!w-full !mb-0"
                inputRef={insuranceLookupRef as RefObject<HTMLInputElement>}
                toggleFavorites={false}
              />
            )}
          />
          <div className="flex items-center gap-1">
            <InfoIcon className="!w-3 !h-3 !text-gray-700" />
            <span className="text-gray-500 text-[11px] font-medium">
            Can&apos;t find your exact insurance?{' '}
              {officePhone ? (
                <a
                  href={`tel:${officePhone}`}
                  className="text-blue-600 hover:underline"
                >
                  Contact the office
                </a>
              ) : (
                'Contact the office'
              )}
              .
            </span>
          </div>
        </div>
        <Input
          value={selectedPayerId ?? formValue.payerEntry?.payerId ?? ''}
          disabled
          id="insurance-payerId"
          className="w-full sm:w-1/3"
          label="Payer ID"
          type="text"
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 w-full sm:items-end">
        <Controller
          name="memberNumber"
          rules={{ required: 'Member ID is required' }}
          control={control}
          render={({ field: { onChange, ...field }, fieldState: { error } }) => (
            <Input
              {...field}
              required
              error={!isNil(error)}
              showErrorText={false}
              className="w-full sm:w-2/3"
              id="insurance-member-number"
              label="Member ID"
              type="text"
              onChange={(value) => {
                onChange(value?.replace(/[^A-Za-z0-9]/g, ''));
              }}
            />
          )}
        />
        <Controller
          name="copay"
          control={control}
          render={({ field }) => (
            <Wrapper
              id="insurance-co-pay"
              className="w-full sm:w-1/3"
              label="Copay"
            >
              <CurrencyTextField {...field} allowNegative />
            </Wrapper>
          )}
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 w-full">
        <Controller
          name="groupName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="insurance-group-name"
              className="w-full sm:w-1/3"
              label="Group Name"
              type="text"
            />
          )}
        />
        <Controller
          name="groupNumber"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="insurance-group-id"
              className="w-full sm:w-1/3"
              label="Group Number"
              type="text"
            />
          )}
        />
        <Controller
          name="policyNumber"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="insurance-policy-number"
              className="w-full sm:w-1/3"
              label="Policy Number"
              type="text"
            />
          )}
        />
      </div>
      <Controller
        control={control}
        name="effectiveFrom"
        rules={{ required: 'Effective from date is required' }}
        render={({ field, fieldState: { invalid } }) => (
          <Wrapper
            id="effective-from"
            error={invalid}
            required
            showErrorText={false}
            label="Effective from"
            className="w-full sm:w-1/2"
          >
            <DatePicker
              {...field}
              containerClassName="w-full"
              className="w-full"
              showDateButtons={false}
              disableFuture
            />
          </Wrapper>
        )}
      />
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <Controller
          name="insuranceSlot"
          control={control}
          rules={{ required: 'Billing order is required' }}
          render={({ field }) => (
            <Select<InsuranceSlot>
              getValue={(option) => option}
              getLabel={(option) => getInsuranceSlotLabel(option)}
              options={insuranceSlotValues}
              placeholder="Select Billing Order"
              id="billing-order"
              label="Billing Order"
              required
              muiClasses={{ select: '!py-[7px]' }}
              className="w-full sm:w-[calc(33.33%-8px)]"
              value={field.value}
              onChange={(value) => {
                field.onChange(value as InsuranceSlot);
              }}
              renderValue={(value) => {
                const insuranceSlotLabel = getInsuranceSlotLabel(value as InsuranceSlot);
                return (
                  <div className={clsx('flex items-center gap-1.5 text-gray-800')}>
                    <span className="font-semibold">
                      {insuranceSlotLabel}
                    </span>
                  </div>
                );
              }}
            >
              {(options, getValue, getLabel) => options.map((option) => {
                const isSelected = option === field.value;
                return (
                  <MenuItem
                    key={getValue(option)}
                    value={getValue(option)}
                    className={clsx(
                      '!mx-1 !my-0.5 !rounded-md !px-2.5 !py-2 !text-xs !bg-transparent',
                      'hover:!bg-blue-50 focus-visible:!bg-blue-50',
                      { '!bg-blue-100/70': isSelected },
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-3">
                      <span className={clsx('text-gray-800', {
                        'font-semibold': isSelected,
                        'font-medium': !isSelected,
                      })}
                      >
                        {getLabel(option)}
                      </span>
                      {isSelected && (
                        <CheckRoundedIcon className="!h-4 !w-4 !text-slate-600" />
                      )}
                    </div>
                  </MenuItem>
                );
              })}
            </Select>
          )}
        />
      </div>
    </div>
  );
}
